import { useCallback, useMemo, useState } from 'react';

import type { Opportunity } from '../../shared/opportunity';
import { useAccountStorage } from '../../shared/account/context';
import type { KeyValueStorage } from '../../shared/account/storage';
import { isPersonal, toOpportunity, type PersonalDraft } from './personalEvents';

/**
 * The user's own events, in localStorage beside their two lists.
 *
 * Drafts are stored rather than the derived records. The record is regenerated
 * on read, so a later change to how one is built — a better area lookup,
 * another default — reaches events that were saved before it, instead of
 * leaving old rows frozen in whatever shape they were written in.
 *
 * This is the only data in the app the user cannot get back from anywhere else:
 * everything on My Schedule is otherwise a reference into listings we still
 * hold. That is why deleting one asks first.
 */

const KEY = 'mcw.personalEvents';

function read(storage: KeyValueStorage): PersonalDraft[] {
  try {
    const raw = storage.getItem(KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    return parsed.filter((d): d is PersonalDraft => {
      if (!d || typeof d !== 'object') return false;
      const draft = d as Partial<PersonalDraft>;
      return (
        typeof draft.id === 'string' &&
        typeof draft.title === 'string' &&
        typeof draft.dayKey === 'string' &&
        typeof draft.startMinutes === 'number' &&
        typeof draft.endMinutes === 'number'
      );
    });
  } catch {
    return [];
  }
}

function write(drafts: PersonalDraft[], storage: KeyValueStorage): void {
  try {
    storage.setItem(KEY, JSON.stringify(drafts));
  } catch {
    // Site data blocked. They last the session.
  }
}

export interface PersonalStore {
  drafts: PersonalDraft[];
  /** Ready to merge with the listings. */
  events: Opportunity[];
  save: (draft: PersonalDraft) => void;
  remove: (id: string) => void;
  find: (id: string) => PersonalDraft | undefined;
}

export function usePersonalEvents(): PersonalStore {
  /** localStorage, or the account once the user has one. */
  const storage = useAccountStorage();
  const [drafts, setDrafts] = useState<PersonalDraft[]>(() => read(storage));

  // The drafts moved: start again from wherever they now live.
  const [source, setSource] = useState(storage);
  if (source !== storage) {
    setSource(storage);
    setDrafts(read(storage));
  }

  const events = useMemo(() => drafts.map(toOpportunity), [drafts]);

  /** One call for both new and edited: the id decides which it is. */
  const save = useCallback((draft: PersonalDraft) => {
    setDrafts((prev) => {
      const at = prev.findIndex((d) => d.id === draft.id);
      const next = at === -1 ? [...prev, draft] : prev.map((d) => (d.id === draft.id ? draft : d));
      write(next, storage);
      return next;
    });
  }, [storage]);

  const remove = useCallback((id: string) => {
    setDrafts((prev) => {
      const next = prev.filter((d) => d.id !== id);
      write(next, storage);
      return next;
    });
  }, [storage]);

  const find = useCallback((id: string) => drafts.find((d) => d.id === id), [drafts]);

  return { drafts, events, save, remove, find };
}

export { isPersonal };
